/**
 * Every button on the V2 surface, so hover / press / disabled styling lives in
 * ONE file (unit convention §3).
 *
 * `variant` is the fill: `primary` is the filled CTA with its glow,
 * `primaryFlat` the same fill without the glow (the strip's "Play here", the
 * notice action), `outline` a `line2` ring that fills with `well` on hover,
 * `ghost` the ring-less version and `danger` the destructive label.
 *
 * `size` is the height: sm 24 (an inline strip action), md 40 (a card action),
 * lg 48 (the result footer), cta 56 (the home CTA, which also takes `meta`).
 *
 * `shape: 'pill'` is the classic lobby's Play button, whose old container radius
 * (32 on a ~44 px button, GameSearch/styles L182-187) is a full round.
 */
import React from 'react';
import {Pressable, StyleSheet} from 'react-native';

import Icon, {IconName} from '../components/Icon';
import {MOTION, RADIUS, SIZE, SPACE} from '../theme/tokens';
import {useV2Theme} from '../theme/useV2Theme';
import Txt, {TxtTone, TxtVariant} from './Txt';

export type ButtonVariant =
  | 'primary'
  | 'primaryFlat'
  | 'outline'
  | 'ghost'
  | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'cta';

/** the result footer's disabled step (05b) */
const DISABLED_OPACITY = 0.45;
/** the CTA's meta line sits ON the primary fill, so it is dimmed (design L3) */
const META_OPACITY = 0.7;
const ICON_SIZE = 16;

const SIZES: {
  [K in ButtonSize]: {
    height: number;
    radius: number;
    paddingHorizontal: number;
    label: TxtVariant;
  };
} = {
  sm: {
    height: SIZE.buttonSm,
    radius: RADIUS.badge,
    paddingHorizontal: SPACE.md,
    label: 'labelSm',
  },
  md: {
    height: SIZE.control,
    radius: RADIUS.control,
    paddingHorizontal: SPACE.lg,
    label: 'label',
  },
  lg: {
    height: SIZE.buttonLg,
    radius: RADIUS.control,
    paddingHorizontal: SPACE.lg,
    label: 'button',
  },
  cta: {
    height: SIZE.cta,
    radius: RADIUS.cta,
    paddingHorizontal: SPACE.xl,
    label: 'cta',
  },
};

const TONES: {[K in ButtonVariant]: TxtTone} = {
  primary: 'onPri',
  primaryFlat: 'onPri',
  outline: 'ink',
  ghost: 'ink',
  danger: 'danger',
};

export type ButtonProps = {
  label: string;
  onPress?: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  /** the CTA's right-hand line ("Blitz · Rated") */
  meta?: string;
  disabled?: boolean;
  /** the result footer weights Rematch 1.3 against the other two */
  flex?: number;
  /** 'pill' = fully rounded (the classic lobby's CTA) */
  shape?: 'default' | 'pill';
  accessibilityLabel?: string;
};

function Button({
  label,
  onPress,
  variant = 'primary',
  size = 'md',
  icon,
  meta,
  disabled,
  flex,
  shape = 'default',
  accessibilityLabel,
}: ButtonProps) {
  const tokens = useV2Theme(s => s.tokens);
  const metrics = SIZES[size];
  const tone = TONES[variant];
  const filled = variant === 'primary' || variant === 'primaryFlat';

  return (
    <Pressable
      disabled={disabled}
      onPress={onPress}
      accessibilityLabel={accessibilityLabel}
      style={({hovered}: any) => [
        styles.button,
        {
          height: metrics.height,
          borderRadius: shape === 'pill' ? RADIUS.pill : metrics.radius,
          paddingHorizontal: metrics.paddingHorizontal,
          justifyContent: meta ? 'space-between' : 'center',
        },
        flex === undefined ? null : {flex},
        filled
          ? ({
              backgroundColor: tokens.pri,
              boxShadow:
                variant === 'primary'
                  ? `0 10px 24px -10px ${tokens.clockGlow}`
                  : undefined,
            } as any)
          : ({
              backgroundColor:
                hovered && !disabled ? tokens.well : 'transparent',
              boxShadow:
                variant === 'ghost'
                  ? undefined
                  : `inset 0 0 0 1px ${tokens.line2}`,
              transitionProperty: 'background-color',
              transitionDuration: `${MOTION.ms}ms`,
              transitionTimingFunction: MOTION.easing,
            } as any),
        disabled ? styles.disabled : null,
      ]}>
      {!!icon && (
        <Icon
          name={icon}
          size={ICON_SIZE}
          color={filled ? tokens.onPri : tokens.ink}
        />
      )}
      <Txt variant={metrics.label} tone={tone}>
        {label}
      </Txt>
      {!!meta && (
        <Txt variant="bodyFlat" tone={tone} style={styles.meta}>
          {meta}
        </Txt>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  disabled: {
    opacity: DISABLED_OPACITY,
  },
  meta: {
    opacity: META_OPACITY,
  },
});

export default React.memo(Button);
